import React, { useMemo, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { hasRole, hasAnyRole, pickPrimaryRole, TierFeatureKey, type RoleCheckable } from '@court-sync/shared';
import { useAuthStore } from '../../store/auth';
import { useCurrentAcademy } from '../../hooks/useDataStore';
import { useCoachAcademyFeatureAccess } from '@/hooks/useFeatureAccess';
import { PageHeader } from '@/components/layout/PageHeader';
import { DashboardTabNav } from '@/components/dashboard/DashboardTabNav';
import SupportTab from './SupportTab';
import SettingsProfileTab from './SettingsProfileTab';
import SettingsPasswordTab from './SettingsPasswordTab';
import SettingsSubscriptionTab from './SettingsSubscriptionTab';
import SettingsNotificationsTab from './SettingsNotificationsTab';
import SettingsGuardianNotificationsTab from './SettingsGuardianNotificationsTab';
import SettingsAdministratorsTab from './SettingsAdministratorsTab';
import { canShowSettingsSubscriptionTab } from './settingsUtils';
import { TierUnlockBadge } from '@/components/tier/TierUnlockBadge';
import { useSettingsTabUnlockTier } from '@/hooks/useTierUnlockBadge';

export type SettingsTabId =
  | 'profile'
  | 'password'
  | 'subscription'
  | 'payments'
  | 'notifications'
  | 'administrators'
  | 'support';

function getValidTab(
  tabFromUrl: string | null,
  userRole: string | undefined,
  user: RoleCheckable | null | undefined
): SettingsTabId {
  if (
    tabFromUrl &&
    ['profile', 'password', 'subscription', 'payments', 'notifications', 'support', 'administrators'].includes(tabFromUrl)
  ) {
    if (
      tabFromUrl === 'subscription' &&
      !canShowSettingsSubscriptionTab(user)
    ) {
      return 'profile';
    }
    if (
      userRole === 'guardian' &&
      !hasRole(user, 'academy_owner') &&
      tabFromUrl === 'subscription'
    ) {
      return 'profile';
    }
    if (
      tabFromUrl === 'notifications' &&
      !hasRole(user, 'academy_owner') &&
      !hasAnyRole(user, ['guardian', 'student'])
    ) {
      return 'profile';
    }
    if (tabFromUrl === 'administrators' && !hasRole(user, 'academy_owner')) return 'profile';
    return tabFromUrl as SettingsTabId;
  }
  return 'profile';
}

export default function Settings() {
  const { user } = useAuthStore();
  const { currentAcademy } = useCurrentAcademy();
  const { hasAccess: canManageAcademyAdmins, tierResolved } = useCoachAcademyFeatureAccess(
    currentAcademy,
    currentAcademy?.slug ?? null,
    TierFeatureKey.ACADEMY_ADMINS
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const tabFromUrl = searchParams.get('tab');

  useEffect(() => {
    if (
      tabFromUrl === 'payments' &&
      user &&
      (hasRole(user, 'academy_owner') || hasRole(user, 'club_owner'))
    ) {
      navigate('/admin/payments?tab=settings', { replace: true });
    }
  }, [tabFromUrl, user, navigate]);

  const administratorsUnlockTier = useSettingsTabUnlockTier('administrators');

  const activeTab = useMemo(() => {
    const primaryRole = user?.rolesWithAcademy?.length ? pickPrimaryRole(user.rolesWithAcademy) : (user?.roles?.[0] ?? 'guardian');
    let valid = getValidTab(tabFromUrl, primaryRole, user);
    if (valid === 'subscription' && !canShowSettingsSubscriptionTab(user)) {
      valid = 'profile';
    }
    if (
      user?.roles?.includes('admin') &&
      (valid === 'subscription' || valid === 'support' || valid === 'notifications' || valid === 'administrators')
    ) {
      return 'profile';
    }
    if (
      valid === 'administrators' &&
      user &&
      hasRole(user, 'academy_owner') &&
      tierResolved &&
      !canManageAcademyAdmins
    ) {
      return 'profile';
    }
    return valid;
  }, [tabFromUrl, user, tierResolved, canManageAcademyAdmins]);

  const tabs = useMemo(() => {
    const list: {
      id: SettingsTabId;
      label: string;
      mobileLabel: string;
      tabId?: string;
      suffix?: React.ReactNode;
    }[] = [
      { id: 'profile', label: 'Account', mobileLabel: 'Account' },
      { id: 'password', label: 'Security', mobileLabel: 'Security' },
    ];
    if (canShowSettingsSubscriptionTab(user)) {
      list.push({ id: 'subscription', label: 'Subscription', mobileLabel: 'Plan' });
    }
    if (hasRole(user, 'academy_owner') || hasAnyRole(user, ['guardian', 'student'])) {
      list.push({ id: 'notifications', label: 'Notifications', mobileLabel: 'Alerts' });
    }
    if (hasRole(user, 'academy_owner')) {
      if (tierResolved && canManageAcademyAdmins) {
        list.push({
          id: 'administrators',
          label: 'Administrators',
          mobileLabel: 'Admins',
          tabId: 'settings-tab-administrators',
          suffix: <TierUnlockBadge unlockTier={administratorsUnlockTier} />,
        });
      }
    }
    if (!user?.roles?.includes('admin')) {
      list.push({ id: 'support', label: 'Support', mobileLabel: 'Support' });
    }
    return list;
  }, [user, tierResolved, canManageAcademyAdmins, administratorsUnlockTier]);

  const handleTabClick = (tabId: SettingsTabId) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', tabId);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="contents">
      <PageHeader
        title="Settings"
        help="Manage your account, security, and preferences"
      />

      <DashboardTabNav<SettingsTabId>
        tabs={tabs}
        activeTab={activeTab}
        onChange={handleTabClick}
        ariaLabel="Settings sections"
      />

      {activeTab === 'profile' && <SettingsProfileTab />}
      {activeTab === 'password' && <SettingsPasswordTab />}
      {activeTab === 'subscription' && <SettingsSubscriptionTab />}
      {activeTab === 'notifications' && (
        <div className="space-y-8">
          {hasRole(user, 'academy_owner') && <SettingsNotificationsTab />}
          {hasAnyRole(user, ['guardian', 'student']) && <SettingsGuardianNotificationsTab />}
        </div>
      )}
      {activeTab === 'administrators' && hasRole(user, 'academy_owner') && (
        <SettingsAdministratorsTab />
      )}
      {activeTab === 'support' && <SupportTab />}
    </div>
  );
}

